import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { LobbyApi, UserRegion } from "@onetasklist/shared/sources/lobby";
import { oidcAuthClient } from "./OidcAuthClient";
import { createDebugLogger } from "@utils/debugLog";
import { subscribeToAppResume } from "@utils/appResume";
import { reloadPage } from "@utils/reloadPage";
import { isEmbedded } from "@/embed/embedHost";
import { clearAiDataOnDevice } from "@/ai/clearAiData";

const log = createDebugLogger("auth:context");

interface User {
  userId: string;
  email: string;
  name: string;
}

export interface AuthContextValue {
  user: User | null;
  accessToken: string | null;
  regionName: string | null;
  regionBaseUrl: string | null;
  availableRegions: UserRegion[];
  isAuthenticated: boolean;
  isLoading: boolean;
  /**
   * The identity server refused to renew a session that is still open. API
   * calls wait until the user signs in again, in a popup, so nothing they were
   * doing is lost.
   */
  isSessionExpired: boolean;
  login: (token: string) => void;
  selectRegion: (regionId: string) => Promise<boolean>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  clearCache: () => Promise<void>;
  clearCacheAndCookies: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

interface AuthProviderProps {
  children: ReactNode;
}

/** The account a token was issued to, or null when it cannot be read. */
const userIdOf = (token: string): string | null => {
  try {
    const decoded = jwtDecode<{ sub?: string; userId?: string }>(token);
    return decoded.sub || decoded.userId || null;
  } catch {
    return null;
  }
};

interface SessionRenewal {
  promise: Promise<string>;
  resolve: (token: string) => void;
}

const isTokenExpired = (token: string): boolean => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (!decoded.exp) return false;
    return decoded.exp * 1000 < Date.now() + 30_000; // 30-second buffer
  } catch {
    return true;
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const location = useLocation();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [regionName, setRegionName] = useState<string | null>(null);
  const [regionBaseUrl, setRegionBaseUrl] = useState<string | null>(null);
  const [availableRegions, setAvailableRegions] = useState<UserRegion[]>([]);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const availableRegionsRef = useRef<UserRegion[]>([]);
  const sessionRef = useRef({
    accessToken: null as string | null,
    regionBaseUrl: null as string | null,
    userId: null as string | null,
  });
  /** Settled by the next sign-in while the session is expired; every held API call waits on it. */
  const sessionRenewalRef = useRef<SessionRenewal | null>(null);
  /** The latest getAccessToken, so the subscription below need not re-register. */
  const getAccessTokenRef = useRef<(() => Promise<string | null>) | null>(null);
  /** Stops the bootstrap below re-entering when its own renewal raises the event. */
  const completingSignInRef = useRef(false);

  useEffect(() => {
    sessionRef.current = {
      accessToken,
      regionBaseUrl,
      userId: user?.userId ?? null,
    };
  }, [accessToken, regionBaseUrl, user]);

  useEffect(() => {
    availableRegionsRef.current = availableRegions;
  }, [availableRegions]);

  /**
   * Flags the open session as expired and returns the token the user's next
   * sign-in brings. Never redirects: that would reload the app and lose
   * whatever the user was doing. The session-expired dialog signs in in a
   * popup instead, from the user's own click, which is the only way a browser
   * lets one open.
   */
  const waitForSessionRenewal = useCallback((): Promise<string> => {
    if (!sessionRenewalRef.current) {
      let resolve: (token: string) => void = () => undefined;
      const promise = new Promise<string>((settle) => {
        resolve = settle;
      });
      sessionRenewalRef.current = { promise, resolve };
    }

    setIsSessionExpired(true);
    return sessionRenewalRef.current.promise;
  }, []);

  // Keep accessToken state in sync with every renewal, whoever triggered it
  // (the renewal timer, a resume, an API call or a SignalR reconnect).
  useEffect(() => {
    return oidcAuthClient.onTokenRenewed((newToken) => {
      const renewal = sessionRenewalRef.current;
      if (renewal) {
        // Someone else signed in to the popup. Everything on screen and every
        // held call belongs to the previous account, so start over cleanly
        // rather than send that work under the new one.
        const previousUserId = sessionRef.current.userId;
        if (previousUserId && userIdOf(newToken) !== previousUserId) {
          log("A different account signed in to renew the session, reloading");
          reloadPage();
          return;
        }

        log("Session renewed interactively, resuming held requests");
        sessionRenewalRef.current = null;
        setIsSessionExpired(false);
        renewal.resolve(newToken);
      }

      setAccessToken(newToken);

      // A popup sign-in hands the session back without changing the route, so
      // the mount-time bootstrap never runs again: the token arrives but the
      // regions it needs are never resolved, isAuthenticated stays false and
      // the embedded host sits on its loading screen until the user reloads.
      // Finishing the bootstrap here is what completes a popup sign-in.
      //
      // A background renewal raises this same event, but only ever on a
      // session that already resolved its region, so it does not come through
      // here. The guard covers the bootstrap's own renewal re-entering.
      if (sessionRef.current.regionBaseUrl || completingSignInRef.current) {
        return;
      }

      completingSignInRef.current = true;
      void Promise.resolve(getAccessTokenRef.current?.()).finally(() => {
        completingSignInRef.current = false;
      });
    });
  }, []);

  // Background renewal is refused when the identity session ended
  // independently of the access token - ask the user to sign in again now,
  // while the current token still works, instead of only finding out once the
  // next API call's own refresh attempt is refused too.
  useEffect(() => {
    return oidcAuthClient.onSilentRenewError((error) => {
      if (!sessionRef.current.regionBaseUrl) {
        return;
      }

      log("Background renewal refused, asking the user to sign in again", error);
      void waitForSessionRenewal();
    });
  }, [waitForSessionRenewal]);

  // Timers do not run while the machine sleeps, so the token can expire
  // unnoticed. Renew as soon as the app is back in view or back online rather
  // than waiting for the first API call to fail.
  useEffect(() => {
    return subscribeToAppResume(() => {
      void oidcAuthClient.renewIfExpiring();
    });
  }, []);

  const lobbyApi = useMemo(
    () =>
      new LobbyApi({
        get: async <T,>(
          url: string,
          config?: { headers?: Record<string, string> },
        ): Promise<{ data: T }> => {
          const response = await fetch(url, {
            method: "GET",
            headers: config?.headers,
            redirect: "manual",
          });

          const isRedirectResponse =
            response.type === "opaqueredirect" ||
            (response.status >= 300 && response.status < 400);

          if (isRedirectResponse) {
            const redirectLocation =
              response.headers.get("Location") ||
              "/Account/Login?ReturnUrl=%2Fme%2Fregions";

            log(
              "Lobby API request redirected to login; using top-level navigation",
              {
                url,
                redirectLocation,
              },
            );

            window.location.assign(redirectLocation);
            throw new Error(`GET ${url} redirected to login`);
          }

          if (!response.ok) {
            throw new Error(`GET ${url} failed with status ${response.status}`);
          }

          return {
            data: (await response.json()) as T,
          };
        },
        post: async <T,>(
          url: string,
          body: unknown,
          config?: { headers?: Record<string, string> },
        ): Promise<{ data: T }> => {
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(config?.headers ?? {}),
            },
            body: JSON.stringify(body),
            redirect: "manual",
          });

          const isRedirectResponse =
            response.type === "opaqueredirect" ||
            (response.status >= 300 && response.status < 400);

          if (isRedirectResponse) {
            const redirectLocation =
              response.headers.get("Location") || "/Account/Login";

            log(
              "Lobby API POST redirected to login; using top-level navigation",
              {
                url,
                redirectLocation,
              },
            );

            window.location.assign(redirectLocation);
            throw new Error(`POST ${url} redirected to login`);
          }

          if (!response.ok) {
            throw new Error(
              `POST ${url} failed with status ${response.status}`,
            );
          }

          return {
            data: (await response.json()) as T,
          };
        },
      }),
    [],
  );

  // Extract user info from token
  const extractUserFromToken = useCallback((token: string): User | null => {
    try {
      const decoded = jwtDecode<{
        sub?: string;
        userId?: string;
        email?: string;
        name?: string;
      }>(token);
      const decodedUserId = decoded.sub || decoded.userId;
      if (!decodedUserId) {
        return null;
      }

      return {
        userId: decodedUserId,
        email: decoded.email || "",
        name: decoded.name || decoded.email || "",
      };
    } catch (error) {
      console.error("[Region Auth] Failed to extract user from token:", error);
      return null;
    }
  }, []);

  const resolveRegions = useCallback(
    async (token: string): Promise<UserRegion[]> => {
      try {
        const regions = await lobbyApi.getUserRegions(token);
        if (!regions.length) {
          console.error("[Region Auth] No regions assigned to this user");
          return [];
        }

        setAvailableRegions(regions);
        return regions;
      } catch (error) {
        console.error("[Region Auth] Failed to load user regions:", error);
        return [];
      }
    },
    [lobbyApi],
  );

  const selectRegionWithToken = useCallback(
    async (
      token: string,
      regionId: string,
      regionsOverride?: UserRegion[],
    ): Promise<boolean> => {
      try {
        const selected = await lobbyApi.selectRegion(regionId, token);
        const sourceRegions = regionsOverride ?? availableRegionsRef.current;
        const selectedRegion = sourceRegions.find(
          (region) => region.regionId === selected.regionId,
        );
        const selectedBaseUrl = selected.regionBaseUrl.replace(/\/$/, "");

        setRegionBaseUrl(selectedBaseUrl);
        setRegionName(selectedRegion?.regionName ?? null);
        return true;
      } catch (error) {
        console.error("[Region Auth] Failed to select region:", error);
        return false;
      }
    },
    [lobbyApi],
  );

  const login = useCallback(
    (token: string) => {
      setAccessToken(token);
      const userInfo = extractUserFromToken(token);
      setUser(userInfo);
    },
    [extractUserFromToken],
  );

  const selectRegion = useCallback(
    async (regionId: string): Promise<boolean> => {
      if (!accessToken) {
        return false;
      }

      return selectRegionWithToken(accessToken, regionId);
    },
    [accessToken, selectRegionWithToken],
  );

  const logout = useCallback(async () => {
    try {
      // Clear local state
      setAccessToken(null);
      setUser(null);
      setRegionName(null);
      setRegionBaseUrl(null);
      setAvailableRegions([]);

      // Before leaving, so nothing the assistant kept is there for the next person. Its own
      // failure must not keep the user signed in.
      await clearAiDataOnDevice().catch(() => undefined);
      await oidcAuthClient.signOut();
    } catch (error) {
      console.error("[Region Auth] Logout failed:", error);
    }
  }, []);

  const clearCache = useCallback(async (): Promise<void> => {
    if (!regionBaseUrl || !accessToken) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${regionBaseUrl}/clearcache`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Clear cache failed");
    }
  }, [regionBaseUrl, accessToken]);

  const clearCacheAndCookies = useCallback(async (): Promise<void> => {
    if (!regionBaseUrl || !accessToken) {
      throw new Error("Not authenticated");
    }

    const response = await fetch(`${regionBaseUrl}/clearcacheandcookies`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Clear cache and cookies failed");
    }
  }, [regionBaseUrl, accessToken]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    const currentSession = sessionRef.current;

    log("getAccessToken called", {
      path: location.pathname,
      hasAccessToken: !!currentSession.accessToken,
      hasRegionBaseUrl: !!currentSession.regionBaseUrl,
    });

    // Return cached token if it's still valid
    if (currentSession.accessToken && currentSession.regionBaseUrl) {
      if (!isTokenExpired(currentSession.accessToken)) {
        log("Using cached auth session");
        return currentSession.accessToken;
      }

      // Token expired mid-session — refresh silently using the stored refresh token
      log("Cached token expired, refreshing silently");
      const refreshed = await oidcAuthClient.requestAccessToken();
      if (refreshed.kind === "token") {
        setAccessToken(refreshed.accessToken);
        return refreshed.accessToken;
      }

      // Right after a wake the network is often not back yet. That is no
      // reason to throw the user out to the sign-in page: the session is
      // intact and renewal keeps retrying in the background.
      if (refreshed.kind === "unavailable") {
        log("Identity server unreachable, keeping the session");
        return null;
      }

      // Hold the request until the user signs in again rather than sending
      // it without a token, so the change it carries is not lost.
      log("Silent refresh refused, holding the request until the user signs in again");
      return waitForSessionRenewal();
    }

    if (oidcAuthClient.isAuthFlowRoute()) {
      log(
        "On auth flow route, skipping token bootstrap",
        {
          path: location.pathname,
        },
      );
      return null;
    }

    setIsLoading(true);

    try {
      const tokenResult = await oidcAuthClient.requestAccessToken();
      if (tokenResult.kind !== "token") {
        if (isEmbedded()) {
          log(
            "No usable OIDC token, leaving sign-in to the embedded gate",
            { reason: tokenResult.kind },
          );
          return null;
        }

        log(
          "No usable OIDC token, starting interactive sign-in",
          { reason: tokenResult.kind },
        );
        await oidcAuthClient.startInteractiveSignIn();
        return null;
      }

      const token = tokenResult.accessToken;

      log("OIDC token retrieved", {
        tokenLength: token.length,
      });

      const regions = await resolveRegions(token);
      if (!regions.length) {
        log("No regions resolved for token");
        return null;
      }

      log("Regions resolved", {
        regionCount: regions.length,
      });

      setAccessToken(token);
      const userInfo = extractUserFromToken(token);
      setUser(userInfo);

      if (regions.length > 1) {
        log(
          "Multiple regions available, waiting for explicit selection",
        );
        setRegionName(null);
        setRegionBaseUrl(null);
        return token;
      }

      // C3 first slice: auto-select first available region.
      const success = await selectRegionWithToken(
        token,
        regions[0].regionId,
        regions,
      );
      if (!success) {
        log("Auto-select region failed", {
          regionId: regions[0].regionId,
        });
        return null;
      }

      log("Auto-select region succeeded", {
        regionId: regions[0].regionId,
      });

      return token;
    } catch (error) {
      console.error("[Region Auth] Error getting access token:", error);
      return null;
    } finally {
      log("getAccessToken completed");
      setIsLoading(false);
    }
  }, [
    location.pathname,
    resolveRegions,
    selectRegionWithToken,
    extractUserFromToken,
    waitForSessionRenewal,
  ]);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  // Initialize authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      if (oidcAuthClient.isAuthFlowRoute()) {
        log("initAuth skipped on auth flow route", {
          path: location.pathname,
          search: location.search,
        });
        setIsLoading(false);
        return;
      }

      log("Initializing authentication...");
      await getAccessToken();
    };

    void initAuth();
  }, [location.pathname, location.search, getAccessToken]);

  const value: AuthContextValue = {
    user,
    accessToken,
    regionName,
    regionBaseUrl,
    availableRegions,
    isAuthenticated: !!user && !!accessToken && !!regionBaseUrl,
    isLoading,
    isSessionExpired,
    login,
    selectRegion,
    logout,
    getAccessToken,
    clearCache,
    clearCacheAndCookies,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
